import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { History, Save, FolderOpen, Trash2, Loader2, Pencil, Check, X } from "lucide-react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function VersionManager({ draft, activeVersionId, setActiveVersionId, onVersionLoaded }) {
  const [open, setOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [busyId, setBusyId] = useState(null);

  const loadVersions = () => {
    setLoading(true);
    api.get("/resume-builder/versions").then((res) => setVersions(res.data)).finally(() => setLoading(false));
  };

  useEffect(() => {
    if (open) loadVersions();
  }, [open]);

  const saveAsNewVersion = async () => {
    if (!newName.trim()) {
      toast.error("Give this version a name (e.g. 'Data Cloud Version')");
      return;
    }
    setSaving(true);
    try {
      await api.post("/resume-builder/versions", { name: newName.trim(), draft });
      setNewName("");
      loadVersions();
      toast.success("Saved as a new version");
    } catch (e) {
      toast.error("Could not save version");
    } finally {
      setSaving(false);
    }
  };

  const loadVersion = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/resume-builder/versions/${id}/load`);
      onVersionLoaded(data);
      setActiveVersionId(id);
      setOpen(false);
      toast.success("Version loaded into editor");
    } catch (e) {
      toast.error("Could not load version");
    } finally {
      setBusyId(null);
    }
  };

  const updateVersionSnapshot = async (id) => {
    setBusyId(id);
    try {
      await api.put(`/resume-builder/versions/${id}`, { draft });
      loadVersions();
      toast.success("Version updated with current edits");
    } catch (e) {
      toast.error("Could not update version");
    } finally {
      setBusyId(null);
    }
  };

  const submitRename = async (id) => {
    if (!renameValue.trim()) {
      setRenamingId(null);
      return;
    }
    try {
      await api.put(`/resume-builder/versions/${id}`, { name: renameValue.trim() });
      setRenamingId(null);
      loadVersions();
    } catch (e) {
      toast.error("Could not rename version");
    }
  };

  const deleteVersion = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/resume-builder/versions/${id}`);
      if (activeVersionId === id) setActiveVersionId(null);
      loadVersions();
      toast.success("Version deleted");
    } catch (e) {
      toast.error("Could not delete version");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="resume-versions-open-button" className="flex items-center gap-1.5 text-xs font-semibold text-amber-300 hover:text-amber-200">
          <History className="w-3.5 h-3.5" /> Version History
        </button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="resume-versions-dialog">
        <DialogHeader>
          <DialogTitle className="text-white">Resume Versions</DialogTitle>
          <DialogDescription>Save multiple resume versions for different roles (e.g. "Data Cloud Version" vs "SFMC Version") and switch between them.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <input
            value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="New version name (e.g. Data Cloud Version)"
            data-testid="resume-version-name-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-400"
          />
          <button onClick={saveAsNewVersion} disabled={saving} data-testid="resume-save-new-version-button" className="flex items-center gap-1.5 bg-amber-500 hover:bg-amber-400 disabled:opacity-60 text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save Current as New
          </button>
        </div>

        <div className="space-y-2 mt-2 max-h-80 overflow-y-auto" data-testid="resume-versions-list">
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : versions.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No saved versions yet — build a resume above, then save it as your first version.</p>
          ) : (
            versions.map((v) => (
              <div key={v.id} className={`bg-white/[0.03] rounded-lg p-3 flex items-center justify-between gap-2 ${activeVersionId === v.id ? "border border-amber-400/40" : ""}`} data-testid={`resume-version-row-${v.id}`}>
                <div className="flex-1 min-w-0">
                  {renamingId === v.id ? (
                    <div className="flex items-center gap-1.5">
                      <input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} data-testid={`resume-version-rename-input-${v.id}`} className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1 text-xs text-white" />
                      <button onClick={() => submitRename(v.id)} data-testid={`resume-version-rename-confirm-${v.id}`}><Check className="w-3.5 h-3.5 text-emerald-400" /></button>
                      <button onClick={() => setRenamingId(null)}><X className="w-3.5 h-3.5 text-slate-400" /></button>
                    </div>
                  ) : (
                    <>
                      <p className="text-sm font-medium text-white truncate">{v.name} {activeVersionId === v.id && <span className="text-[10px] text-amber-400 font-normal">(active)</span>}</p>
                      <p className="text-[11px] text-slate-400 truncate">{v.target_role || "No target role set"}</p>
                    </>
                  )}
                </div>
                {renamingId !== v.id && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => loadVersion(v.id)} disabled={busyId === v.id} data-testid={`resume-version-load-${v.id}`} title="Load into editor" className="text-slate-300 hover:text-amber-300 disabled:opacity-50">
                      <FolderOpen className="w-4 h-4" />
                    </button>
                    <button onClick={() => updateVersionSnapshot(v.id)} disabled={busyId === v.id} title="Update with current edits" data-testid={`resume-version-update-${v.id}`} className="text-slate-300 hover:text-indigo-300 disabled:opacity-50">
                      <Save className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setRenamingId(v.id); setRenameValue(v.name); }} title="Rename" data-testid={`resume-version-rename-btn-${v.id}`} className="text-slate-300 hover:text-white">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteVersion(v.id)} disabled={busyId === v.id} title="Delete" data-testid={`resume-version-delete-${v.id}`} className="text-slate-300 hover:text-red-400 disabled:opacity-50">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
